import { useParams } from "react-router-dom";
import {
  getVehicleById,
  updateVehicle,
  submitPhotosToVehicle,
} from "../../services/vehicles";
import { getBookedDatesByBookableId } from "../../services/bookings";
import {
  deleteUnavailableDateRangesForBookable,
  getUnavailableDateRangesForBookable,
  bulkCreateUnavailableDateRangesForBookable,
} from "../../services/unavailable_dates";
import VehicleForm from "../../components/vehicle-form/VehicleForm";
import { useQuery } from "@tanstack/react-query";
import { useEntityUpdate, usePhotoUpload } from "../../utils/sharedMutations";
import { getApiErrorMessage, preparePhotosForUpload } from "../../utils/helpers";
import {
  normalizeReservations,
  ownerBlockTuplesEqual,
  ownerBlocksFromApi,
  sanitizeOwnerBlocksForSave,
} from "../../utils/vehicleStockOccupancy";
import { BOOKING_TYPES } from "../../constants";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { DateObject } from "react-multi-date-picker";

const fmt = (value) => new DateObject(value).format();

const normalizePricePerDay = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return value;

  // API may return localized currency text (e.g. "EUR 38.00").
  const numeric = parseFloat(value.replace(/[^0-9.-]+/g, ""));
  return Number.isNaN(numeric) ? value : numeric;
};

function EditVehicle() {
  const { vehicleId } = useParams();
  const toast = useToast();
  const { t } = useTranslation("common");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["vehicles", vehicleId],
    queryFn: () => getVehicleById(vehicleId),
    select: (data) => data.data,
    onSuccess: () => {
      refetchUnavailableDateRanges();
      refetchBookedDates();
    },
  });

  const {
    data: dateRanges,
    isLoading: isLoadingDateRanges,
    refetch: refetchUnavailableDateRanges,
  } = useQuery({
    queryKey: ["vehicleDateRanges", data],
    queryFn: () => getUnavailableDateRangesForBookable(data.id, BOOKING_TYPES.CAR),
    enabled: !!data,
    select: (data) => data.data.results,
  });

  const {
    data: bookedDates,
    isLoading: isLoadingBookedDates,
    refetch: refetchBookedDates,
  } = useQuery({
    queryKey: ["vehicleBookedDates", data],
    queryFn: () => getBookedDatesByBookableId(data.id, BOOKING_TYPES.CAR),
    enabled: !!data,
    select: (data) => data.data.results,
  });

  const submitPhotosMutation = usePhotoUpload(({ vehicleId, photos }) =>
    submitPhotosToVehicle(vehicleId, photos)
  );

  const editVehicleMutation = useEntityUpdate((vehicleData) => {
    delete vehicleData.id;
    return updateVehicle(vehicleId, {
      ...vehicleData,
    });
  });

  const onSubmit = async (vehicleData, images, unavailableDateRanges) => {
    const savedStock = Number(data?.stockCount) || 1;
    const newStock = Number(vehicleData.stockCount) || 1;
    const initialBlocks = ownerBlocksFromApi(dateRanges, savedStock, fmt);
    const blocksUnchanged = ownerBlockTuplesEqual(
      initialBlocks,
      unavailableDateRanges
    );

    const vehicle = await editVehicleMutation.mutateAsync(vehicleData);
    if (images.length > 0) {
      await submitPhotosMutation.mutateAsync({
        vehicleId: vehicle.data.id,
        photos: preparePhotosForUpload(images),
      });
    }

    if (!blocksUnchanged) {
      try {
        const reservations = normalizeReservations(bookedDates, fmt);
        const sanitized = sanitizeOwnerBlocksForSave(
          unavailableDateRanges,
          newStock,
          reservations
        );
        await deleteUnavailableDateRangesForBookable(
          vehicleId,
          BOOKING_TYPES.CAR
        );
        await bulkCreateUnavailableDateRangesForBookable(
          vehicleId,
          BOOKING_TYPES.CAR,
          sanitized
        );
      } catch (error) {
        toast({
          title: t("unavailableDatesSaveError"),
          description: getApiErrorMessage(
            error,
            t("unavailableDatesSaveError")
          ),
          status: "error",
          duration: 7000,
          isClosable: true,
          position: "top",
        });
        refetch();
        return;
      }
    }

    refetch();
  };

  if (isLoading || isLoadingDateRanges || isLoadingBookedDates) {
    return <div>Loading...</div>;
  }

  return (
    <VehicleForm
      editable
      vehicleData={{
        ...data,
        pricePerDay: normalizePricePerDay(data?.defaultPrice ?? data?.pricePerDay),
        vehicleSeasonalPricesAttributes: data.seasonalPrices ?? [],
      }}
      onSubmit={onSubmit}
      isSubmitting={
        editVehicleMutation.isLoading || submitPhotosMutation.isLoading
      }
      showUnavailableDatesCalendar={true}
      bookedDates={bookedDates}
      dateRangesFromDatabase={dateRanges}
    />
  );
}

export default EditVehicle;
